import { Accordion, Badge, Divider, Flex, Group, Stack, Text } from '@mantine/core';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import StepExecutionsRenderer from '@/pages/Run/components/StepExecutionsRenderer.tsx';
import { AutomationStep } from '@/types/automationSteps.ts';
import { Run } from '@/types/runs.ts';
import { ParentStepRun } from '@/types/stepExecutions.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import type { RetryAttemptOutcomeTone } from '@/pages/Run/components/retryAttemptPresentation.ts';
import type { AutoExpandPlan } from '@/types/autoExpandPlan.ts';
import {
  HistoricalScopePresentationProvider,
  useIsHistoricalScopePresentation,
} from '@/pages/Run/components/HistoricalScopePresentationContext.ts';

type ChildScopeAccordionItemProps = {
  value: string;
  label: string;
  status?: ExecutionStatus | null;
  run: Run;
  enabled: boolean;
  isOpen: boolean;
  showDivider?: boolean;
  parentSteps: ParentStepRun[];
  stepsById: Record<string, AutomationStep>;
  parentPath?: string;
  childIndex: number;
  attemptNumber?: number;
  scopeParentStepRunId?: string;
  outcomeLabel?: string;
  outcomeTone?: RetryAttemptOutcomeTone;
  detailText?: string;
  lifecycleText?: string;
  historical?: boolean;
  autoExpandPlan?: AutoExpandPlan | null;
};

const ChildScopeAccordionItem = ({
  value,
  label,
  status,
  run,
  enabled,
  isOpen,
  showDivider = false,
  parentSteps,
  stepsById,
  parentPath,
  childIndex,
  attemptNumber,
  scopeParentStepRunId,
  outcomeLabel,
  outcomeTone,
  detailText,
  lifecycleText,
  historical,
  autoExpandPlan,
}: ChildScopeAccordionItemProps) => {
  const isHistoricalScopePresentation = useIsHistoricalScopePresentation();

  return (
    <>
      {showDivider && <Divider mb={'0'} />}
      <Accordion.Item value={value} style={{ border: 0 }} my={'2px'}>
        <Accordion.Control>
          <Group align={'flex-start'} gap={8} wrap={'nowrap'}>
            <StatusIndicator status={status} include_text={false} />
            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
              <Group align={'center'} gap={6} wrap={'wrap'}>
                <Text fw={500} size={'sm'}>
                  {label}
                </Text>
                {outcomeLabel && (
                  <Badge size="xs" variant="light" color={getOutcomeBadgeColor(outcomeTone)}>
                    {outcomeLabel}
                  </Badge>
                )}
              </Group>
              {detailText && (
                <Text size="xs" c="dimmed" lineClamp={2}>
                  {detailText}
                </Text>
              )}
              {lifecycleText && (
                <Text size="xs" c="dimmed">
                  {lifecycleText}
                </Text>
              )}
            </Stack>
          </Group>
        </Accordion.Control>
        <Accordion.Panel styles={{ content: { padding: 0 } }}>
          {isOpen && (
            <Flex
              style={{
                marginTop: '30px',
                marginBottom: '10px',
                paddingLeft: '8px',
                paddingRight: '8px',
                width: '100%',
                minWidth: 0,
              }}
            >
              <HistoricalScopePresentationProvider value={isHistoricalScopePresentation || !!historical}>
                <StepExecutionsRenderer
                  run={run}
                  enabled={enabled}
                  parentSteps={parentSteps}
                  stepsById={stepsById}
                  parentPath={parentPath}
                  childIndex={childIndex}
                  {...(attemptNumber !== undefined ? { attemptNumber } : {})}
                  scopeParentStepRunId={scopeParentStepRunId}
                  autoExpandPlan={autoExpandPlan}
                />
              </HistoricalScopePresentationProvider>
            </Flex>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </>
  );
};

const getOutcomeBadgeColor = (tone?: RetryAttemptOutcomeTone) => {
  switch (tone) {
    case 'green':
      return 'green';
    case 'red':
      return 'red';
    case 'yellow':
      return 'yellow';
    case 'blue':
      return 'blue';
    default:
      return 'gray';
  }
};

export default ChildScopeAccordionItem;
